// "Heute relevant" — Dark.

import Card from "../Card";
import { BriefingEntry, TodayBriefingResponse, formatDate } from "../../lib/api";

interface Props {
  data: TodayBriefingResponse;
}

const FIRMA_BADGE: Record<string, string> = {
  DrainX: "bg-emerald-500/15 text-emerald-300",
  "G&K Sanitärtechnik": "bg-indigo-500/15 text-indigo-300",
  "G&K Sanitaertechnik": "bg-indigo-500/15 text-indigo-300",
  InfraX: "bg-rose-500/15 text-rose-300",
};

function firmaBadge(firma: string): string {
  return FIRMA_BADGE[firma] ?? "bg-slate-700/50 text-slate-400";
}

function timeOfDay(iso: string): string {
  const m = iso.match(/T(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : "";
}

// T2.13-B #6: Drei (oder mehr) Punkte → typografischer Ellipsis. Anwenden
// auf alle Mail-Briefing-Textfelder (summary, action_hint), nicht nur summary.
function replaceEllipsis(s: string): string {
  return s.replace(/\.{3,}/g, "…");
}

export default function TodayBriefingCard({ data }: Props) {
  if (!data.available) {
    return (
      <Card title="Heute relevant" accent="neutral">
        <p className="text-slate-500">{data.setup_hint ?? "Daten nicht verfügbar."}</p>
      </Card>
    );
  }
  const total =
    data.heute_kritisch.length + data.diese_woche.length + data.nachverfolgen.length;

  if (data.run_date === null) {
    return (
      <Card title="Heute relevant" accent="neutral">
        <p className="text-slate-500">
          {data.setup_hint ?? "Wird beim nächsten 06:30-Briefing-Lauf befüllt."}
        </p>
      </Card>
    );
  }

  const subtitle =
    total === 0
      ? `Lauf ${formatDate(data.run_date)} · ruhig`
      : `${total} · Lauf ${formatDate(data.run_date)}`;
  const accent =
    data.heute_kritisch.length > 0
      ? "red"
      : data.diese_woche.length > 0
        ? "yellow"
        : "green";

  return (
    <Card title="Heute relevant" subtitle={subtitle} accent={accent}>
      <Section
        title="Heute kritisch"
        emptyHint="Nichts Kritisches"
        items={data.heute_kritisch}
        accent="text-rose-300"
      />
      <Section
        title="Diese Woche"
        emptyHint="Nichts"
        items={data.diese_woche}
        accent="text-amber-300"
      />
      <Section
        title="Nachverfolgen"
        emptyHint="Nichts"
        items={data.nachverfolgen}
        accent="text-slate-400"
      />
    </Card>
  );
}

function Section({
  title,
  emptyHint,
  items,
  accent,
}: {
  title: string;
  emptyHint: string;
  items: BriefingEntry[];
  accent: string;
}) {
  return (
    <div className="mt-3 first:mt-0">
      <h3 className={`text-[10px] font-medium uppercase tracking-wide mb-1 ${accent}`}>
        {title} · {items.length}
      </h3>
      {items.length === 0 ? (
        <p className="text-[11px] text-slate-600 pl-1">{emptyHint}</p>
      ) : (
        <ul className="space-y-2">
          {items.slice(0, 3).map((e) => (
            <Entry
              key={`${e.mailbox_slug}-${e.from_address}-${e.received_at}`}
              e={e}
            />
          ))}
          {items.length > 3 && (
            <li className="text-[11px] text-sky-300/80 pl-1">
              +{items.length - 3} weitere — in Hamburger/Detail
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

function Entry({ e }: { e: BriefingEntry }) {
  const sender = e.from_name || e.from_address;
  return (
    <li className="text-sm">
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-[10px] tabular-nums text-slate-500">
          {timeOfDay(e.received_at)}
        </span>
        {e.firma_tag && (
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded ${firmaBadge(e.firma_tag)}`}
          >
            {e.firma_tag === "G&K Sanitaertechnik" ? "G+K" : e.firma_tag}
          </span>
        )}
        <span className="font-medium text-slate-200 truncate">{sender}</span>
      </div>
      <p
        className="text-slate-300 leading-snug text-xs"
        title={e.summary}
      >
        {replaceEllipsis(e.summary)}
      </p>
      {e.action_hint && (
        <p
          className="text-[11px] font-medium text-slate-200 mt-0.5"
          title={e.action_hint}
        >
          → {replaceEllipsis(e.action_hint)}
        </p>
      )}
      {e.gmail_deeplink && (
        <a
          href={e.gmail_deeplink}
          target="_blank"
          rel="noreferrer"
          className="text-[11px] text-sky-400 hover:underline"
        >
          Original
        </a>
      )}
    </li>
  );
}
